import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatInt } from '../lib/format';

/**
 * Shared list pagination (#12) — one control for every admin list, rather than
 * seven near-identical ones.
 *
 * **It paginates the FILTERED array, in the browser.** That is a deliberate choice
 * at this data size, not an oversight:
 *
 *   - Every admin list filters, searches and sorts client-side today, and the
 *     Payments page sums its Paid / Pending / Refunded totals from the filtered
 *     set. Server-side paging without ALSO moving all of that server-side would
 *     make filters apply to only the visible page and turn those totals into
 *     "totals of whatever is on screen" — a money bug, in exchange for a
 *     performance win nothing currently needs.
 *   - The largest of these tables on production is 380 rows (users); the rest are
 *     117–268. Fetching them whole costs nothing measurable.
 *
 * What this DOES fix is the rendering and reading problem: a 380-row table is
 * unusable, and it grows without bound.
 *
 * **When to move it server-side:** once any of these lists passes a few thousand
 * rows. At that point the filters, the search and the sort must move to the API
 * in the SAME change — pagination alone would silently break them. Audit log and
 * notifications will get there first.
 */

/** The choices offered by the per-page control. 25 is the default (locked decision). */
export const PER_PAGE_OPTIONS = [25, 50, 100, 200] as const;
export const DEFAULT_PER_PAGE = 25;

export interface PaginationState<T> {
  /** The slice to render. */
  pageItems: T[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  setPage: (p: number) => void;
  setPerPage: (n: number) => void;
  /** Index of the first/last visible row, 1-based, for "Showing X–Y of Z". */
  from: number;
  to: number;
}

/**
 * Paginate an already-filtered array.
 *
 * The important part is the effect: when the caller's filters change, `items`
 * shrinks and the current page can fall off the end. Left alone that shows an
 * empty table on a list that plainly has results — the classic pagination bug.
 * The page is clamped back into range whenever the row count changes.
 */
export function usePagination<T>(items: T[], perPageDefault: number = DEFAULT_PER_PAGE): PaginationState<T> {
  const [page, setPage] = useState(1);
  const [perPage, setPerPageRaw] = useState(perPageDefault);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), Math.max(1, Math.ceil(items.length / perPage))));
  }, [items.length, perPage]);

  // Changing rows-per-page keeps you near what you were reading rather than
  // dumping you back to page 1: the first visible row stays visible.
  const setPerPage = (n: number) => {
    const firstVisible = (page - 1) * perPage;
    setPerPageRaw(n);
    setPage(Math.floor(firstVisible / n) + 1);
  };

  const pageItems = useMemo(() => items.slice((page - 1) * perPage, page * perPage), [items, page, perPage]);

  return {
    pageItems,
    page,
    perPage,
    total,
    totalPages,
    setPage,
    setPerPage,
    from: total === 0 ? 0 : (page - 1) * perPage + 1,
    to: Math.min(page * perPage, total),
  };
}

/**
 * Page numbers to render, with gaps collapsed to `null` (an ellipsis).
 * Always shows the first and last page plus a window around the current one, so
 * the control's width does not depend on how many pages there are.
 */
function pageWindow(page: number, totalPages: number): Array<number | null> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const out: Array<number | null> = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) out.push(null);
  for (let i = start; i <= end; i++) out.push(i);
  if (end < totalPages - 1) out.push(null);
  out.push(totalPages);
  return out;
}

const btn =
  'inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-md text-sm border transition ' +
  'border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 ' +
  'hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:pointer-events-none';

/**
 * The footer control. Renders nothing at all when everything already fits on one
 * page AND the default page size has not been changed — a "1 of 1" pager under a
 * six-row table is noise.
 */
export function Pagination<T>({ state, label = 'rows' }: { state: PaginationState<T>; label?: string }) {
  const { page, perPage, total, totalPages, setPage, setPerPage, from, to } = state;
  if (total <= PER_PAGE_OPTIONS[0] && perPage === DEFAULT_PER_PAGE) return null;

  return (
    <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 dark:border-zinc-700">
      <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
        <span className="tabular-nums">
          {total === 0 ? `No ${label}` : `Showing ${formatInt(from)}–${formatInt(to)} of ${formatInt(total)} ${label}`}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
          <span className="hidden sm:inline">Rows</span>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="h-8 px-2 rounded-md text-sm bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200"
            aria-label="Rows per page"
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <nav className="flex items-center gap-1" aria-label="Pagination">
          <button type="button" className={btn} onClick={() => setPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
            <ChevronLeft className="w-4 h-4" />
          </button>

          {pageWindow(page, totalPages).map((p, i) =>
            p === null ? (
              <span key={`gap-${i}`} className="px-1 text-sm text-zinc-400 dark:text-zinc-500" aria-hidden="true">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                aria-current={p === page ? 'page' : undefined}
                className={
                  p === page
                    ? 'inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-md text-sm border border-brand bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 dark:border-brand-dm tabular-nums'
                    : `${btn} tabular-nums`
                }
              >
                {p}
              </button>
            ),
          )}

          <button type="button" className={btn} onClick={() => setPage(page + 1)} disabled={page >= totalPages} aria-label="Next page">
            <ChevronRight className="w-4 h-4" />
          </button>
        </nav>
      </div>
    </div>
  );
}
